import React, { useState, useEffect, useCallback } from 'react';
import AnimatedPage from '@/components/AnimatedPage';
import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle } from '@easyflow/ui-master/components/ui/glass-card';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Input } from '@easyflow/ui-master/components/ui/input';
import { Textarea } from '@easyflow/ui-master/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@easyflow/ui-master/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-master/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@easyflow/ui-master/components/ui/dialog';
import { Checkbox } from '@easyflow/ui-master/components/ui/checkbox';
import {
  Ticket,
  Search,
  UserCheck,
  UserX,
  MessageSquare,
  Lock,
  Eye,
  Clock,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  BarChart3,
  Timer,
  Send,
  GripVertical,
  Loader2,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  Building2
} from 'lucide-react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid
} from 'recharts';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
  useDroppable
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import apiClient from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

interface TicketStatus {
  id: string;
  name: string;
  sort_order: number;
  color: string;
  is_default: boolean;
  is_closed: boolean;
}

interface TicketItem {
  id: string;
  tenant_id: string;
  tenant_name: string;
  tenant_slug: string;
  subject: string;
  description: string;
  urgency: string;
  category: string | null;
  url_path: string;
  status_id: string;
  status_name: string;
  status_color: string;
  assigned_to_user_id: string | null;
  assigned_to_name: string | null;
  creator_name: string;
  note_count: number;
  created_at: string;
  updated_at: string;
  first_response_at: string | null;
  resolved_at: string | null;
}

interface TicketNote {
  id: string;
  content: string;
  is_private: boolean;
  author_name: string;
  author_is_master: boolean;
  created_at: string;
}

interface AnalyticsData {
  by_status: { name: string; count: number; color: string }[];
  by_urgency: { urgency: string; count: number }[];
  by_tenant: { tenant_name: string; count: number }[];
  totals: {
    total_open: number;
    total_closed: number;
    created_today: number;
    created_this_week: number;
    created_this_month: number;
  };
}

interface SlaData {
  avg_first_response_minutes: number;
  avg_resolution_hours: number;
  pct_resolved_24h: number;
  pct_resolved_48h: number;
  tickets_overdue: number;
}

const CATEGORY_CONFIG: Record<string, { label: string; color: string; bg: string; icon: string }> = {
  bug: { label: 'Bug', color: 'text-red-700 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-500/20', icon: '🐛' },
  feature_request: { label: 'Funzionalità', color: 'text-blue-700 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-500/20', icon: '✨' },
  question: { label: 'Domanda', color: 'text-cyan-700 dark:text-cyan-400', bg: 'bg-cyan-50 dark:bg-cyan-500/20', icon: '❓' },
  operational: { label: 'Operativo', color: 'text-foreground dark:text-muted-foreground/70', bg: 'bg-muted dark:bg-muted/500/20', icon: '⚙️' },
};

const URGENCY_FALLBACK = { label: 'Bassa', color: 'text-foreground', bg: 'bg-muted' };
const URGENCY_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  low: { label: 'Bassa', color: 'text-green-700 dark:text-green-400', bg: 'bg-green-100 dark:bg-green-500/20' },
  medium: { label: 'Media', color: 'text-amber-700 dark:text-amber-400', bg: 'bg-amber-100 dark:bg-amber-500/20' },
  high: { label: 'Alta', color: 'text-orange-700 dark:text-orange-400', bg: 'bg-orange-100 dark:bg-orange-500/20' },
  critical: { label: 'Critica', color: 'text-red-700 dark:text-red-400', bg: 'bg-red-100 dark:bg-red-500/20' },
};

const URGENCY_CHART_COLORS: Record<string, string> = {
  low: '#22c55e',
  medium: '#f59e0b',
  high: '#f97316',
  critical: '#ef4444',
};

function formatRelativeTime(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMin / 60);
  const diffD = Math.floor(diffH / 24);

  if (diffMin < 1) return 'ora';
  if (diffMin < 60) return `${diffMin}m fa`;
  if (diffH < 24) return `${diffH}h fa`;
  if (diffD < 30) return `${diffD}gg fa`;
  const diffMon = Math.floor(diffD / 30);
  return `${diffMon} mesi fa`;
}

function DroppableColumn({ statusId, children }: { statusId: string; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: statusId });

  return (
    <div
      ref={setNodeRef}
      className={`flex-1 min-w-[280px] max-w-[340px] flex flex-col transition-colors duration-200 rounded-xl ${
        isOver ? 'bg-purple-50 dark:bg-purple-500/10 ring-2 ring-purple-400/50' : ''
      }`}
    >
      {children}
    </div>
  );
}

function DraggableTicketCard({
  ticket,
  currentUserId,
  onAssign,
  onClick,
}: {
  ticket: TicketItem;
  currentUserId: string;
  onAssign: (id: string) => void;
  onClick: (ticket: TicketItem) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: ticket.id,
    data: { type: 'ticket', ticket, statusId: ticket.status_id },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const urgency = URGENCY_CONFIG[ticket.urgency] ?? URGENCY_CONFIG.low ?? URGENCY_FALLBACK;
  const category = ticket.category ? CATEGORY_CONFIG[ticket.category] : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white dark:bg-muted/80 rounded-lg border border-border dark:border-white/10 p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
      onClick={() => onClick(ticket)}
    >
      <div className="flex items-start gap-2">
        <div
          {...attributes}
          {...listeners}
          className="mt-1 cursor-grab active:cursor-grabbing text-muted-foreground/70 dark:text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-foreground truncate">{ticket.subject}</p>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-border dark:border-white/20 text-muted-foreground dark:text-muted-foreground/70">
              <Building2 className="h-2.5 w-2.5 mr-0.5" />
              {ticket.tenant_name}
            </Badge>
            <Badge className={`text-[10px] px-1.5 py-0 ${urgency.bg} ${urgency.color} border-0`}>
              {urgency.label}
            </Badge>
            {category && (
              <Badge className={`text-[10px] px-1.5 py-0 ${category.bg} ${category.color} border-0`}>
                {category.icon} {category.label}
              </Badge>
            )}
          </div>
          <div className="flex items-center justify-between mt-2 text-[11px] text-muted-foreground dark:text-muted-foreground">
            <span>{ticket.creator_name}</span>
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-0.5">
                <Clock className="h-3 w-3" />
                {formatRelativeTime(ticket.created_at)}
              </span>
              {ticket.note_count > 0 && (
                <span className="flex items-center gap-0.5">
                  <MessageSquare className="h-3 w-3" />
                  {ticket.note_count}
                </span>
              )}
            </div>
          </div>
          <div className="mt-2 pt-2 border-t border-slate-100 dark:border-white/5">
            {!ticket.assigned_to_user_id ? (
              <Button
                size="sm"
                variant="outline"
                className="h-6 text-[10px] px-2 border-purple-300 dark:border-purple-500/30 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-500/10"
                onClick={(e) => {
                  e.stopPropagation();
                  onAssign(ticket.id);
                }}
              >
                <UserCheck className="h-3 w-3 mr-1" />
                Prendi in carico
              </Button>
            ) : ticket.assigned_to_user_id === currentUserId ? (
              <Badge className="text-[10px] bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-400 border-0">
                <UserCheck className="h-3 w-3 mr-1" />
                Assegnato a te
              </Badge>
            ) : (
              <span className="text-[11px] text-muted-foreground dark:text-muted-foreground flex items-center gap-1">
                <UserCheck className="h-3 w-3" />
                {ticket.assigned_to_name}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function TicketCardOverlay({ ticket }: { ticket: TicketItem }) {
  const urgency = URGENCY_CONFIG[ticket.urgency] ?? URGENCY_CONFIG.low ?? URGENCY_FALLBACK;

  return (
    <div className="bg-white dark:bg-muted rounded-lg border-2 border-purple-400 p-3 shadow-2xl w-[280px] rotate-2">
      <p className="font-semibold text-sm text-foreground truncate">{ticket.subject}</p>
      <div className="flex gap-1.5 mt-1.5">
        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
          {ticket.tenant_name}
        </Badge>
        <Badge className={`text-[10px] px-1.5 py-0 ${urgency.bg} ${urgency.color} border-0`}>
          {urgency.label}
        </Badge>
      </div>
    </div>
  );
}

function TicketDetailDialog({
  ticket,
  open,
  onClose,
  currentUserId,
  onAssign,
  onUnassign,
  onRefresh,
}: {
  ticket: TicketItem | null;
  open: boolean;
  onClose: () => void;
  currentUserId: string;
  onAssign: (id: string) => void;
  onUnassign: (id: string) => void;
  onRefresh: () => void;
}) {
  const [notes, setNotes] = useState<TicketNote[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [sendingNote, setSendingNote] = useState(false);

  useEffect(() => {
    if (ticket && open) {
      void loadTicketDetail(ticket.id);
    }
  }, [ticket?.id, open]);

  const loadTicketDetail = async (id: string) => {
    setLoadingNotes(true);
    try {
      const res = await apiClient.get<{ ticket: TicketItem; notes: TicketNote[] }>(`/master/support/tickets/${id}`);
      if (res.success && res.data) {
        setNotes(res.data.notes || []);
      }
    } catch {
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleSendNote = async () => {
    if (!ticket || !newNote.trim()) return;
    setSendingNote(true);
    try {
      const res = await apiClient.post<TicketNote>(`/master/support/tickets/${ticket.id}/notes`, {
        content: newNote.trim(),
        is_private: isPrivate,
      });
      if (res.success && res.data) {
        setNotes((prev) => [...prev, res.data!]);
        setNewNote('');
        setIsPrivate(false);
        onRefresh();
      }
    } catch {
    } finally {
      setSendingNote(false);
    }
  };

  if (!ticket) return null;

  const urgency = URGENCY_CONFIG[ticket.urgency] ?? URGENCY_CONFIG.low ?? URGENCY_FALLBACK;
  const category = ticket.category ? CATEGORY_CONFIG[ticket.category] : undefined;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-white dark:bg-slate-900 border-border dark:border-white/10">
        <DialogHeader>
          <DialogTitle className="text-foreground text-lg">{ticket.subject}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="text-muted-foreground dark:text-muted-foreground/70">
              <Building2 className="h-3 w-3 mr-1" />
              {ticket.tenant_name}
            </Badge>
            <Badge className={`${urgency.bg} ${urgency.color} border-0`}>{urgency.label}</Badge>
            {category && (
              <Badge className={`${category.bg} ${category.color} border-0`}>
                {category.icon} {category.label}
              </Badge>
            )}
            <Badge
              style={{ backgroundColor: ticket.status_color + '20', color: ticket.status_color }}
              className="border-0"
            >
              {ticket.status_name}
            </Badge>
          </div>

          {ticket.description && (
            <div className="bg-muted/50 dark:bg-muted/50 rounded-lg p-3 text-sm text-foreground dark:text-muted-foreground/60 whitespace-pre-wrap">
              {ticket.description}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-muted-foreground dark:text-muted-foreground">Creato da:</span>
              <span className="ml-2 text-foreground">{ticket.creator_name}</span>
            </div>
            <div>
              <span className="text-muted-foreground dark:text-muted-foreground">Data:</span>
              <span className="ml-2 text-foreground">
                {new Date(ticket.created_at).toLocaleString('it-IT')}
              </span>
            </div>
            {ticket.url_path && (
              <div className="col-span-2">
                <span className="text-muted-foreground dark:text-muted-foreground">Percorso:</span>
                <span className="ml-2 text-foreground font-mono text-xs">{ticket.url_path}</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 py-2 border-y border-slate-100 dark:border-white/10">
            <span className="text-sm text-muted-foreground dark:text-muted-foreground">Assegnato a:</span>
            {ticket.assigned_to_user_id ? (
              <div className="flex items-center gap-2">
                <Badge className="bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-400 border-0">
                  {ticket.assigned_to_user_id === currentUserId ? 'Te' : ticket.assigned_to_name}
                </Badge>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-500/10"
                  onClick={() => onUnassign(ticket.id)}
                >
                  <UserX className="h-3 w-3 mr-1" />
                  Rimuovi
                </Button>
              </div>
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs border-purple-300 dark:border-purple-500/30 text-purple-600 dark:text-purple-400"
                onClick={() => onAssign(ticket.id)}
              >
                <UserCheck className="h-3 w-3 mr-1" />
                Prendi in carico
              </Button>
            )}
          </div>

          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Note ({notes.length})
            </h4>

            {loadingNotes ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-purple-500" />
              </div>
            ) : notes.length === 0 ? (
              <p className="text-sm text-muted-foreground dark:text-muted-foreground text-center py-4">Nessuna nota</p>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {notes.map((note) => (
                  <div
                    key={note.id}
                    className={`rounded-lg p-3 text-sm ${
                      note.is_private
                        ? 'bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20'
                        : 'bg-muted/50 dark:bg-muted/50 border border-border dark:border-white/10'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-foreground text-xs flex items-center gap-1">
                        {note.is_private ? (
                          <Lock className="h-3 w-3 text-amber-500" />
                        ) : (
                          <Eye className="h-3 w-3 text-muted-foreground/70" />
                        )}
                        {note.author_name}
                        {note.author_is_master && (
                          <Badge className="text-[9px] px-1 py-0 bg-purple-100 dark:bg-purple-500/20 text-purple-600 dark:text-purple-400 border-0 ml-1">
                            Master
                          </Badge>
                        )}
                      </span>
                      <span className="text-[10px] text-muted-foreground/70 dark:text-muted-foreground">
                        {formatRelativeTime(note.created_at)}
                      </span>
                    </div>
                    <p className="text-foreground dark:text-muted-foreground/60 whitespace-pre-wrap">{note.content}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-3 space-y-2">
              <Textarea
                placeholder="Scrivi una nota..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                className="min-h-[80px] bg-white dark:bg-muted border-border dark:border-white/10 text-foreground"
              />
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="is_private"
                    checked={isPrivate}
                    onCheckedChange={(checked) => setIsPrivate(checked === true)}
                  />
                  <label htmlFor="is_private" className="text-xs text-muted-foreground dark:text-muted-foreground/70 flex items-center gap-1 cursor-pointer">
                    <Lock className="h-3 w-3" />
                    Nota privata
                  </label>
                </div>
                <Button
                  size="sm"
                  disabled={!newNote.trim() || sendingNote}
                  onClick={handleSendNote}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  {sendingNote ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                  Invia
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function KanbanTab() {
  const { user } = useAuth();
  const currentUserId = user?.id || '';

  const [statuses, setStatuses] = useState<TicketStatus[]>([]);
  const [tickets, setTickets] = useState<TicketItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [urgencyFilter, setUrgencyFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [activeTicket, setActiveTicket] = useState<TicketItem | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<TicketItem | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: '200' });
      if (searchQuery) params.set('search', searchQuery);
      if (urgencyFilter !== 'all') params.set('urgency', urgencyFilter);
      if (categoryFilter !== 'all') params.set('category', categoryFilter);

      const [statusRes, ticketRes] = await Promise.all([
        apiClient.get<TicketStatus[]>('/master/support/statuses'),
        apiClient.get<{ tickets: TicketItem[]; total: number }>(`/master/support/tickets?${params.toString()}`),
      ]);

      if (statusRes.success && statusRes.data) {
        const sorted = [...(statusRes.data)].sort((a, b) => a.sort_order - b.sort_order);
        setStatuses(sorted);
      }
      if (ticketRes.success && ticketRes.data) {
        setTickets(Array.isArray(ticketRes.data) ? ticketRes.data as TicketItem[] : []);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [searchQuery, urgencyFilter, categoryFilter]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const handleAssign = async (ticketId: string) => {
    try {
      const res = await apiClient.patch(`/master/support/tickets/${ticketId}/assign`);
      if (res.success) {
        setTickets((prev) =>
          prev.map((t) =>
            t.id === ticketId
              ? { ...t, assigned_to_user_id: currentUserId, assigned_to_name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() }
              : t
          )
        );
      }
    } catch {
    }
  };

  const handleUnassign = async (ticketId: string) => {
    try {
      const res = await apiClient.patch(`/master/support/tickets/${ticketId}/unassign`);
      if (res.success) {
        setTickets((prev) =>
          prev.map((t) =>
            t.id === ticketId ? { ...t, assigned_to_user_id: null, assigned_to_name: null } : t
          )
        );
      }
    } catch {
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const ticket = tickets.find((t) => t.id === event.active.id);
    if (ticket) setActiveTicket(ticket);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveTicket(null);
    const { active, over } = event;
    if (!over) return;

    const ticketId = active.id as string;
    const ticket = tickets.find((t) => t.id === ticketId);
    if (!ticket) return;

    let newStatusId = over.id as string;
    const overTicket = tickets.find((t) => t.id === over.id);
    if (overTicket) {
      newStatusId = overTicket.status_id;
    }

    if (ticket.status_id === newStatusId) return;

    const targetStatus = statuses.find((s) => s.id === newStatusId);
    if (!targetStatus) return;

    setTickets((prev) =>
      prev.map((t) =>
        t.id === ticketId
          ? { ...t, status_id: newStatusId, status_name: targetStatus.name, status_color: targetStatus.color }
          : t
      )
    );

    try {
      await apiClient.patch(`/master/support/tickets/${ticketId}/status`, { status_id: newStatusId });
    } catch {
      void loadData();
    }
  };

  const handleDragOver = (_event: DragOverEvent) => {};

  const openTicketDetail = (ticket: TicketItem) => {
    setSelectedTicket(ticket);
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex gap-4 overflow-x-auto pb-4 pt-2">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="min-w-[280px] flex-1">
            <div className="h-10 bg-muted dark:bg-white/10 rounded-t-xl animate-pulse" />
            <div className="space-y-3 p-3">
              {[1, 2, 3].map((j) => (
                <div key={j} className="h-28 bg-muted dark:bg-white/5 rounded-lg animate-pulse" />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/70" />
          <Input
            placeholder="Cerca ticket..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-white dark:bg-muted border-border dark:border-white/10"
          />
        </div>
        <Select value={urgencyFilter} onValueChange={setUrgencyFilter}>
          <SelectTrigger className="w-40 bg-white dark:bg-muted border-border dark:border-white/10">
            <SelectValue placeholder="Urgenza" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le urgenze</SelectItem>
            <SelectItem value="low">Bassa</SelectItem>
            <SelectItem value="medium">Media</SelectItem>
            <SelectItem value="high">Alta</SelectItem>
            <SelectItem value="critical">Critica</SelectItem>
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-40 bg-white dark:bg-muted border-border dark:border-white/10">
            <SelectValue placeholder="Categoria" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le categorie</SelectItem>
            <SelectItem value="bug">Bug</SelectItem>
            <SelectItem value="feature_request">Funzionalità</SelectItem>
            <SelectItem value="question">Domanda</SelectItem>
            <SelectItem value="operational">Operativo</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
      >
        <div className="flex gap-4 overflow-x-auto pb-4">
          {statuses.map((status) => {
            const columnTickets = tickets.filter((t) => t.status_id === status.id);
            return (
              <DroppableColumn key={status.id} statusId={status.id}>
                <div
                  className="rounded-t-xl px-3 py-2.5 flex items-center justify-between"
                  style={{ backgroundColor: status.color + '15' }}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: status.color }} />
                    <span className="font-semibold text-sm text-foreground">{status.name}</span>
                  </div>
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-white/60 dark:bg-white/10">
                    {columnTickets.length}
                  </Badge>
                </div>
                <div className="flex-1 p-2 space-y-2 min-h-[100px] bg-muted/50/50 dark:bg-muted/30 rounded-b-xl">
                  <SortableContext items={columnTickets.map((t) => t.id)} strategy={verticalListSortingStrategy}>
                    {columnTickets.map((ticket) => (
                      <DraggableTicketCard
                        key={ticket.id}
                        ticket={ticket}
                        currentUserId={currentUserId}
                        onAssign={handleAssign}
                        onClick={openTicketDetail}
                      />
                    ))}
                  </SortableContext>
                  {columnTickets.length === 0 && (
                    <div className="flex items-center justify-center h-20 text-xs text-muted-foreground/70 dark:text-muted-foreground">
                      Nessun ticket
                    </div>
                  )}
                </div>
              </DroppableColumn>
            );
          })}
        </div>

        <DragOverlay>
          {activeTicket ? <TicketCardOverlay ticket={activeTicket} /> : null}
        </DragOverlay>
      </DndContext>

      <TicketDetailDialog
        ticket={selectedTicket}
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSelectedTicket(null);
        }}
        currentUserId={currentUserId}
        onAssign={async (id) => {
          await handleAssign(id);
          setSelectedTicket((prev) =>
            prev?.id === id
              ? { ...prev, assigned_to_user_id: currentUserId, assigned_to_name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() }
              : prev
          );
        }}
        onUnassign={async (id) => {
          await handleUnassign(id);
          setSelectedTicket((prev) =>
            prev?.id === id ? { ...prev, assigned_to_user_id: null, assigned_to_name: null } : prev
          );
        }}
        onRefresh={loadData}
      />
    </>
  );
}

function AnalisiTab() {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<AnalyticsData>('/master/support/analytics');
      if (res.success && res.data) {
        setData(res.data);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-24 bg-muted dark:bg-white/5 rounded-xl animate-pulse" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-72 bg-muted dark:bg-white/5 rounded-xl animate-pulse" />
          <div className="h-72 bg-muted dark:bg-white/5 rounded-xl animate-pulse" />
        </div>
      </div>
    );
  }

  if (!data) return null;

  const statCards = [
    { label: 'Total aperti', value: data.totals.total_open, icon: AlertCircle, color: 'text-blue-500' },
    { label: 'Total chiusi', value: data.totals.total_closed, icon: CheckCircle2, color: 'text-green-500' },
    { label: 'Creati oggi', value: data.totals.created_today, icon: TrendingUp, color: 'text-purple-500' },
    { label: 'Creati settimana', value: data.totals.created_this_week, icon: BarChart3, color: 'text-amber-500' },
    { label: 'Creati mese', value: data.totals.created_this_month, icon: Ticket, color: 'text-indigo-500' },
  ];

  const urgencyChartData = (data.by_urgency || []).map((u) => ({
    name: URGENCY_CONFIG[u.urgency]?.label || u.urgency,
    value: u.count,
    fill: URGENCY_CHART_COLORS[u.urgency] || '#6b7280',
  }));

  const tenantChartData = (data.by_tenant || []).slice(0, 10);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <GlassCard key={card.label} className="backdrop-blur-sm bg-white/60 dark:bg-white/5 border-border dark:border-white/20">
              <GlassCardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg bg-muted ${card.color}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{card.value}</p>
                    <p className="text-xs text-muted-foreground dark:text-muted-foreground/70">{card.label}</p>
                  </div>
                </div>
              </GlassCardContent>
            </GlassCard>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <GlassCard className="backdrop-blur-sm bg-white/60 dark:bg-white/5 border-border dark:border-white/20">
          <GlassCardHeader>
            <GlassCardTitle className="text-foreground text-base">Ticket per stato</GlassCardTitle>
          </GlassCardHeader>
          <GlassCardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data.by_status || []} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.2)" />
                <XAxis type="number" tick={{ fontSize: 12 }} stroke="currentColor" />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={100} stroke="currentColor" />
                <Tooltip contentStyle={{ background: 'rgba(255,255,255,0.95)', borderRadius: '8px', border: 'none' }} />
                <Bar dataKey="count" name="Ticket" radius={[0, 4, 4, 0]}>
                  {(data.by_status || []).map((entry, index) => (
                    <Cell key={index} fill={entry.color || '#6b7280'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </GlassCardContent>
        </GlassCard>

        <GlassCard className="backdrop-blur-sm bg-white/60 dark:bg-white/5 border-border dark:border-white/20">
          <GlassCardHeader>
            <GlassCardTitle className="text-foreground text-base">Ticket per urgenza</GlassCardTitle>
          </GlassCardHeader>
          <GlassCardContent>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={urgencyChartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={(entry) => `${entry.name}: ${entry.value}`}
                >
                  {urgencyChartData.map((entry, index) => (
                    <Cell key={index} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </GlassCardContent>
        </GlassCard>
      </div>

      <GlassCard className="backdrop-blur-sm bg-white/60 dark:bg-white/5 border-border dark:border-white/20">
        <GlassCardHeader>
          <GlassCardTitle className="text-foreground text-base">Top 10 tenant per ticket</GlassCardTitle>
        </GlassCardHeader>
        <GlassCardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={tenantChartData} margin={{ bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.2)" />
              <XAxis
                dataKey="tenant_name"
                tick={{ fontSize: 10 }}
                angle={-45}
                textAnchor="end"
                height={80}
                stroke="currentColor"
              />
              <YAxis tick={{ fontSize: 12 }} stroke="currentColor" />
              <Tooltip contentStyle={{ background: 'rgba(255,255,255,0.95)', borderRadius: '8px', border: 'none' }} />
              <Bar dataKey="count" name="Ticket" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </GlassCardContent>
      </GlassCard>
    </div>
  );
}

function SlaTab() {
  const [data, setData] = useState<SlaData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void loadSla();
  }, []);

  const loadSla = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<SlaData>('/master/support/sla');
      if (res.success && res.data) {
        setData(res.data);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-40 bg-muted dark:bg-white/5 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  if (!data) return null;

  const getResponseColor = (minutes: number) => {
    if (minutes < 30) return { ring: 'ring-green-400', text: 'text-green-500', bg: 'bg-green-500', progress: 'bg-green-500' };
    if (minutes < 60) return { ring: 'ring-green-400', text: 'text-green-500', bg: 'bg-green-500', progress: 'bg-green-500' };
    if (minutes < 120) return { ring: 'ring-amber-400', text: 'text-amber-500', bg: 'bg-amber-500', progress: 'bg-amber-500' };
    return { ring: 'ring-red-400', text: 'text-red-500', bg: 'bg-red-500', progress: 'bg-red-500' };
  };

  const getResolutionColor = (hours: number) => {
    if (hours < 24) return { ring: 'ring-green-400', text: 'text-green-500', bg: 'bg-green-500', progress: 'bg-green-500' };
    if (hours < 48) return { ring: 'ring-amber-400', text: 'text-amber-500', bg: 'bg-amber-500', progress: 'bg-amber-500' };
    return { ring: 'ring-red-400', text: 'text-red-500', bg: 'bg-red-500', progress: 'bg-red-500' };
  };

  const getPctColor = (pct: number) => {
    if (pct >= 80) return { ring: 'ring-green-400', text: 'text-green-500', bg: 'bg-green-500', progress: 'bg-green-500' };
    if (pct >= 60) return { ring: 'ring-amber-400', text: 'text-amber-500', bg: 'bg-amber-500', progress: 'bg-amber-500' };
    return { ring: 'ring-red-400', text: 'text-red-500', bg: 'bg-red-500', progress: 'bg-red-500' };
  };

  const responseColor = getResponseColor(data.avg_first_response_minutes);
  const resolutionColor = getResolutionColor(data.avg_resolution_hours);
  const pct24Color = getPctColor(data.pct_resolved_24h);
  const pct48Color = getPctColor(data.pct_resolved_48h);

  const metrics = [
    {
      label: 'Tempo medio prima risposta',
      value: `${Math.round(data.avg_first_response_minutes)} min`,
      color: responseColor,
      icon: Timer,
      pct: Math.min(100, Math.max(0, 100 - (data.avg_first_response_minutes / 120) * 100)),
    },
    {
      label: 'Tempo medio risoluzione',
      value: `${data.avg_resolution_hours.toFixed(1)} ore`,
      color: resolutionColor,
      icon: Clock,
      pct: Math.min(100, Math.max(0, 100 - (data.avg_resolution_hours / 72) * 100)),
    },
    {
      label: '% Risolti entro 24h',
      value: `${data.pct_resolved_24h.toFixed(1)}%`,
      color: pct24Color,
      icon: CheckCircle2,
      pct: data.pct_resolved_24h,
    },
    {
      label: '% Risolti entro 48h',
      value: `${data.pct_resolved_48h.toFixed(1)}%`,
      color: pct48Color,
      icon: CheckCircle2,
      pct: data.pct_resolved_48h,
    },
    {
      label: 'Ticket in ritardo',
      value: data.tickets_overdue.toString(),
      color: data.tickets_overdue > 0
        ? { ring: 'ring-red-400', text: 'text-red-500', bg: 'bg-red-500', progress: 'bg-red-500' }
        : { ring: 'ring-green-400', text: 'text-green-500', bg: 'bg-green-500', progress: 'bg-green-500' },
      icon: AlertTriangle,
      pct: data.tickets_overdue > 0 ? 100 : 0,
      isOverdue: true,
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
      {metrics.map((metric) => {
        const Icon = metric.icon;
        return (
          <GlassCard
            key={metric.label}
            className={`backdrop-blur-sm bg-white/60 dark:bg-white/5 border-border dark:border-white/20 ${
              metric.isOverdue && data.tickets_overdue > 0 ? 'ring-2 ring-red-400/50 dark:ring-red-500/30' : ''
            }`}
          >
            <GlassCardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className={`p-1.5 rounded-lg bg-muted ${metric.color.text}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <p className="text-xs text-muted-foreground dark:text-muted-foreground/70 leading-tight">{metric.label}</p>
              </div>

              <p className={`text-2xl font-bold ${metric.color.text} mb-3`}>{metric.value}</p>

              {!metric.isOverdue && (
                <div className="w-full h-2 bg-muted dark:bg-white/10 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${metric.color.progress}`}
                    style={{ width: `${Math.min(100, metric.pct)}%` }}
                  />
                </div>
              )}

              {metric.isOverdue && data.tickets_overdue > 0 && (
                <div className="flex items-center gap-1 text-xs text-red-500">
                  <TrendingDown className="h-3 w-3" />
                  Richiede attenzione
                </div>
              )}
              {metric.isOverdue && data.tickets_overdue === 0 && (
                <div className="flex items-center gap-1 text-xs text-green-500">
                  <CheckCircle2 className="h-3 w-3" />
                  Tutto in regola
                </div>
              )}
            </GlassCardContent>
          </GlassCard>
        );
      })}
    </div>
  );
}

export default function TicketManagement() {
  const [activeTab, setActiveTab] = useState('kanban');

  return (
    <AnimatedPage>
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600 dark:from-cyan-400 dark:to-purple-400">
            Gestione Ticket
          </h1>
          <p className="text-muted-foreground dark:text-muted-foreground/70 mt-1">
            Gestione cross-tenant dei ticket di supporto
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="kanban" className="flex items-center gap-2">
              <Ticket className="h-4 w-4" />
              Kanban
            </TabsTrigger>
            <TabsTrigger value="analisi" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Analisi
            </TabsTrigger>
            <TabsTrigger value="sla" className="flex items-center gap-2">
              <Timer className="h-4 w-4" />
              SLA
            </TabsTrigger>
          </TabsList>

          <TabsContent value="kanban">
            <KanbanTab />
          </TabsContent>

          <TabsContent value="analisi">
            {activeTab === 'analisi' && <AnalisiTab />}
          </TabsContent>

          <TabsContent value="sla">
            {activeTab === 'sla' && <SlaTab />}
          </TabsContent>
        </Tabs>
      </div>
    </AnimatedPage>
  );
}
